import { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';
import { Button } from './ui/button';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Native apps (iOS/Android via Capacitor) must NOT show a web-style cookie
    // banner. A cookie/tracking prompt implies tracking and triggers App Store
    // Guideline 5.1.2(i) (App Tracking Transparency). SmartKick does not track
    // users for advertising in the native app, so we suppress the banner there.
    const isNative = typeof window !== 'undefined'
      && window.Capacitor
      && typeof window.Capacitor.isNativePlatform === 'function'
      && window.Capacitor.isNativePlatform();
    if (isNative) return;

    // Check if user has already consented
    const consent = localStorage.getItem('smartkick_cookie_consent');
    if (!consent) {
      // Delay showing the banner slightly for better UX
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('smartkick_cookie_consent', JSON.stringify({
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const acceptEssential = () => {
    localStorage.setItem('smartkick_cookie_consent', JSON.stringify({
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-4xl mx-auto bg-zinc-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden">
        <div className="p-4 sm:p-6">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex w-12 h-12 rounded-full bg-amber-500/20 items-center justify-center flex-shrink-0">
              <Cookie className="w-6 h-6 text-amber-400" />
            </div>
            
            <div className="flex-1">
              <h3 className="text-lg font-bold text-white mb-2">We Use Cookies</h3>
              <p className="text-zinc-400 text-sm mb-4">
                We use cookies to enhance your experience, analyze site traffic, and for marketing purposes. 
                By clicking "Accept All", you consent to our use of cookies. You can also choose to accept 
                only essential cookies required for the site to function.
              </p>
              
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={acceptAll}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium"
                  data-testid="accept-all-cookies"
                >
                  Accept All
                </Button>
                <Button
                  onClick={acceptEssential}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  data-testid="accept-essential-cookies"
                >
                  Essential Only
                </Button>
                <a 
                  href="/app/privacy" 
                  className="text-sm text-zinc-400 hover:text-white underline self-center"
                >
                  Privacy Policy
                </a>
              </div>
            </div>
            
            <button 
              onClick={acceptEssential}
              className="text-zinc-500 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
